import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAudit } from '../context/AuditContext';
import { PageFrame } from '../components/PageFrame';
import { SectionCard } from '../components/SectionCard';
import { NavigationControls } from '../components/NavigationControls';
import { OpportunityMatrix } from '../components/OpportunityMatrix';
import { 
  AlertCircle, CheckCircle2, 
  Calendar, ArrowUpRight, FileText, Download, Target, List
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { motion } from 'framer-motion';
import { trackPDFDownloaded, trackBookRoadmapClicked, trackScheduleCallClicked } from '../utils/analytics';
import { formatCategoryLabel } from '../utils/labels';
import { ReportGenerationProgress } from '../components/ReportGenerationProgress';
import { PremiumReportDashboard } from '../components/PremiumReportDashboard';
import { getBackendUrl } from '../utils/apiBase';
import { auditSessionHeaders, getAuditSessionId } from '../components/ProtectedAuditRoute';

const BACKEND_URL = getBackendUrl();

const stripCodeFences = (text = '') => text.replace(/```markdown\n?/g, '').replace(/```\n?/g, '');

const renderInlineText = (text = '') => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const renderStructuredText = (text = '') => (
  <div className="space-y-2">
    {stripCodeFences(text).split('\n').filter(line => line.trim()).map((line, index) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('## ')) {
        return <h2 key={index} className="text-xl font-bold text-primary mt-5">{trimmed.replace(/^##\s*/, '')}</h2>;
      }
      if (trimmed.startsWith('### ')) {
        return <h3 key={index} className="inline-block bg-primary text-white rounded-full px-4 py-2 text-sm font-bold mt-3">{trimmed.replace(/^###\s*/, '')}</h3>;
      }
      if (/^[-*•]\s+/.test(trimmed)) {
        return <p key={index} className="text-base text-foreground leading-relaxed pl-4">• {renderInlineText(trimmed.replace(/^[-*•]\s+/, ''))}</p>;
      }
      if (/^\d+\.\s+/.test(trimmed)) {
        return <p key={index} className="text-base text-foreground leading-relaxed">{renderInlineText(trimmed)}</p>;
      }
      return <p key={index} className="text-base text-foreground leading-relaxed">{renderInlineText(trimmed)}</p>;
    })}
  </div>
);

const getExecutiveSummaryCards = (summary = '') => {
  const clean = stripCodeFences(summary).replace(/\s+/g, ' ').trim();
  if (!clean) return [];
  const sentences = clean.match(/[^.!?]+[.!?]+/g) || [clean];
  const labels = ['What matters most', 'Why this is useful', 'What to do next'];
  return labels.map((label, index) => ({
    label,
    text: (sentences[index] || sentences[sentences.length - 1] || '').trim()
  })).filter(card => card.text);
};

const getActionPlanPhases = (text = '') => {
  const lines = stripCodeFences(text).split('\n').map(line => line.trim()).filter(Boolean);
  const phases = [];
  let current = null;

  lines.forEach(line => {
    const heading = line.replace(/^#{2,3}\s*/, '').replace(/^\*\*|\*\*$/g, '').trim();
    const isPhaseHeading = /^#{2,3}\s+/.test(line) || /^(days?\s*\d|week\s*\d|phase\s*\d|first\s*30|next\s*30|final\s*30|30\s*days|60\s*days|90\s*days)/i.test(heading);

    if (isPhaseHeading) {
      current = { title: heading, items: [] };
      phases.push(current);
      return;
    }

    if (!current) {
      current = { title: 'Start here', items: [] };
      phases.push(current);
    }

    current.items.push(line.replace(/^[-*•]\s+/, '').replace(/^\d+\.\s+/, ''));
  });

  if (phases.length <= 1 && lines.length > 0) {
    const chunks = [
      { title: 'Days 1–14: Prove the pilot', items: lines.slice(0, Math.ceil(lines.length / 3)) },
      { title: 'Days 15–45: Build the workflow', items: lines.slice(Math.ceil(lines.length / 3), Math.ceil((lines.length * 2) / 3)) },
      { title: 'Days 46–90: Decide what scales', items: lines.slice(Math.ceil((lines.length * 2) / 3)) }
    ];
    return chunks.filter(phase => phase.items.length > 0);
  }

  return phases.slice(0, 5);
};

const ActionPlanTimeline = ({ actionPlan }) => {
  const phases = getActionPlanPhases(actionPlan);
  return (
    <div className="space-y-5">
      <div className="rounded-3xl border border-[#c7e6ea] bg-[linear-gradient(135deg,#f6fbfc_0%,#ffffff_55%,#eef9f8_100%)] p-6">
        <Badge className="mb-3 bg-[#1c334a] text-white">90-day execution map</Badge>
        <h2 className="text-3xl md:text-4xl font-black tracking-tight text-[#1c334a]">Turn the first pilot into a real business decision.</h2>
        <p className="mt-3 max-w-3xl text-lg leading-relaxed text-[#4b5f73]">
          This is organized as a short proof path, not a wall of tasks. Each phase should answer one question: should we stop, adjust, or scale?
        </p>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        {phases.map((phase, index) => (
          <div key={`${phase.title}-${index}`} className="relative rounded-3xl border border-[#dbe7e9] bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#049aaa] text-lg font-black text-white">{index + 1}</div>
              <h3 className="text-xl font-black leading-tight text-[#1c334a]">{phase.title}</h3>
            </div>
            <ul className="space-y-3">
              {phase.items.slice(0, 5).map((item, itemIndex) => (
                <li key={`${item}-${itemIndex}`} className="flex gap-3 text-sm leading-relaxed text-[#4b5f73]">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#049aaa]" />
                  <span>{renderInlineText(item)}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function ReportPage({ testMode = false, testData = null }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { auditData, getOverallScore } = useAudit();
  
  const [report, setReport] = useState(null);
  const [fullAudit, setFullAudit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const auditId = searchParams.get('audit_id');
  
  // Use fetched audit data if available, otherwise fall back to context
  const displayData = fullAudit || auditData;
  const overallScore = fullAudit?.summary?.overall || getOverallScore();

  useEffect(() => {
    if (testMode && testData) {
      setFullAudit(testData.audit);
      setReport(testData.report);
      setError(null);
      setLoading(false);
      return;
    }

    if (auditId) {
      generateReport();
    } else {
      setError('No audit ID provided');
      setLoading(false);
    }
  }, [auditId, testMode, testData]);

  const generateReport = async () => {
    try {
      setLoading(true);
      
      const sessionHeaders = auditSessionHeaders();
      // First, fetch the audit data
      const auditResponse = await fetch(`${BACKEND_URL}/api/audit/${auditId}`, {
        headers: sessionHeaders
      });
      if (!auditResponse.ok) {
        throw new Error('Audit not found');
      }
      const auditData = await auditResponse.json();
      setFullAudit(auditData);
      
      // Then generate/fetch the report
      const response = await fetch(`${BACKEND_URL}/api/audit/${auditId}/generate-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...sessionHeaders }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate report');
      }
      
      const data = await response.json();
      setReport(data.report);
      setError(null);
    } catch (err) {
      console.error('Error generating report:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageFrame pageNumber={10} backgroundStyle="light">
        <div className="min-h-screen flex items-center justify-center px-6">
          <ReportGenerationProgress companyName={displayData?.company || 'your business'} />
        </div>
      </PageFrame>
    );
  }

  if (error) {
    return (
      <PageFrame pageNumber={10} backgroundStyle="light">
        <div className="min-h-screen flex items-center justify-center">
          <div className="max-w-md text-center space-y-4">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
            <h2 className="text-2xl font-bold text-foreground">Unable to Generate Report</h2>
            <p className="text-muted-foreground">{error}</p>
            <Button onClick={generateReport}>Try Again</Button>
          </div>
        </div>
      </PageFrame>
    );
  }

  return (
    <PageFrame pageNumber={10} backgroundStyle="light">
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative overflow-hidden rounded-3xl border border-[#dbe7e9] bg-white p-8 md:p-10 text-left shadow-[0_30px_60px_-35px_rgba(28,51,74,0.35)] mb-6"
          >
            <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#049aaa] via-[#d8e005] to-[#1c334a]" />
            <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-[#1c334a] mb-4" data-testid="report-title">
              Your First Safe AI Workflow
            </h1>
            <p className="text-[#4b5f73] text-xl md:text-2xl leading-relaxed max-w-4xl mb-5">
              A practical dashboard for choosing one wise, supervised AI pilot, naming what not to automate yet, and proving value in 14 days.
            </p>
            
            {/* Company info badges */}
            <div className="flex flex-wrap items-center gap-3 mt-5">
              {displayData?.company && (
                <Badge className="bg-[#049aaa] text-white px-4 py-2 text-sm">
                  {displayData.company}
                </Badge>
              )}
              {displayData?.contact?.industry && (
                <Badge variant="secondary" className="px-4 py-2 text-sm">
                  {displayData.contact.industry.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </Badge>
              )}
              {displayData?.contact?.employeeCount && (
                <Badge variant="outline" className="border-[#9ccfd4] text-[#049aaa] bg-white px-4 py-2 text-sm">
                  {displayData.contact.employeeCount} Employees
                </Badge>
              )}
            </div>
          </motion.div>

          <PremiumReportDashboard auditData={displayData} report={report} overallScore={overallScore} />

          {/* Summary scan cards */}
          <SectionCard title="What this report is saying" icon={FileText} className="mt-6">
            <div className="grid gap-4 md:grid-cols-3">
              {getExecutiveSummaryCards(report?.executive_summary || 'No summary available').map((card, index) => (
                <div key={`${card.label}-${index}`} className="rounded-3xl border border-[#dbe7e9] bg-[#f6fbfc] p-5">
                  <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#1c334a] text-sm font-black text-white">{index + 1}</div>
                  <h3 className="text-xl font-black tracking-tight text-[#1c334a]">{card.label}</h3>
                  <p className="mt-3 text-base leading-relaxed text-[#4b5f73]">{renderInlineText(card.text)}</p>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* 90-Day Action Plan */}
          {report?.action_plan && (
            <SectionCard title="Your 90-Day Action Plan" icon={Calendar} className="mt-6">
              <ActionPlanTimeline actionPlan={report.action_plan} />
            </SectionCard>
          )}

          {/* All Opportunities Identified */}
          {displayData?.roi_items && displayData.roi_items.length > 0 && (
            <SectionCard title="All Opportunities Identified" icon={List} className="mt-6">
              <p className="text-sm text-muted-foreground mb-4">
                Complete list of tasks and work responsibilities you identified:
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task/Work Responsibility</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-center">Quadrant</TableHead>
                    <TableHead className="text-right">Annual Cost</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayData.roi_items.map((item, idx) => {
                    const matrixItem = displayData.matrix_items?.find(m => m.roi_index === idx);
                    const impact = matrixItem?.impact || 0;
                    const difficulty = item.difficulty || 3;
                    let quadrant = '';
                    let quadrantColor = '';
                    
                    if (impact > 0) {
                      if (impact > 3 && difficulty <= 3) {
                        quadrant = 'Quick Wins';
                        quadrantColor = 'bg-accent text-accent-foreground';
                      } else if (impact > 3 && difficulty > 3) {
                        quadrant = 'Strategic';
                        quadrantColor = 'bg-primary text-white';
                      } else if (impact <= 3 && difficulty <= 3) {
                        quadrant = 'Fill-Ins';
                        quadrantColor = 'bg-muted text-foreground';
                      } else {
                        quadrant = 'Time Sinks';
                        quadrantColor = 'bg-gray-400 text-gray-700';
                      }
                    }
                    
                    return (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.process}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{formatCategoryLabel(item.category)}</TableCell>
                        <TableCell className="text-center">
                          {quadrant ? (
                            <Badge className={`${quadrantColor} whitespace-nowrap text-xs`}>{quadrant}</Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">Not rated</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-primary">${item.annual.toLocaleString()}</TableCell>
                      </TableRow>
                    );
                  })}
                  <TableRow className="bg-primary/5 font-bold">
                    <TableCell colSpan={3}>Total Annual Cost</TableCell>
                    <TableCell className="text-right text-primary text-lg">
                      ${displayData.roi_items.reduce((sum, item) => sum + item.annual, 0).toLocaleString()}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </SectionCard>
          )}

          {/* Strategic Prioritization Map */}
          {displayData?.matrix_items && displayData.matrix_items.length > 0 && (
            <SectionCard title="Strategic Prioritization Map" icon={Target} className="mt-6">
              <p className="text-sm text-muted-foreground mb-4">
                Visual map showing where each opportunity falls based on impact and difficulty. Focus on the <strong>Quick Wins</strong> quadrant for immediate value.
              </p>
              <OpportunityMatrix 
                opportunities={displayData.roi_items.map((item, idx) => {
                  const matrixItem = displayData.matrix_items.find(m => m.roi_index === idx);
                  return {
                    roi_index: idx,
                    name: item.process,
                    category: item.category,
                    annual: item.annual,
                    difficulty: item.difficulty || 3,
                    impact: matrixItem?.impact || 0,
                    effort: item.difficulty || 3
                  };
                }).filter(opp => opp.impact > 0)}
              />
            </SectionCard>
          )}

          {/* Key Frictions Analysis */}
          {report?.key_frictions && (
            <SectionCard title="Key Frictions by Lens" icon={AlertCircle} className="mt-6">
              <div className="prose prose-sm max-w-none text-foreground leading-relaxed">
                {renderStructuredText(report.key_frictions)}
              </div>
            </SectionCard>
          )}

          {/* Download & Next Steps */}
          <div className="bg-white rounded-lg shadow-lg p-8 mt-6">
            <h2 className="text-2xl font-bold text-secondary mb-4">What's Next?</h2>
            
            <div className="mb-6 max-w-full">
              <p className="text-lg text-foreground leading-relaxed mb-3">
                <strong className="font-bold text-primary">From clarity to momentum — and resilience that lasts.</strong>
              </p>
              <p className="text-base text-muted-foreground">
                Save your report for reference, then book the full AI Opportunity Roadmap™ to transform today's insights into a practical strategy that drives measurable results across your organization.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Button
                size="lg"
                variant="outline"
                className="w-full py-6 gap-2"
                onClick={() => {
                  trackPDFDownloaded(auditId);
                  window.open(`${BACKEND_URL}/api/audit/${auditId}/download-pdf?session_id=${encodeURIComponent(getAuditSessionId())}`, '_blank');
                }}
                data-testid="download-pdf-button"
              >
                <Download className="h-5 w-5" />
                Download PDF Report
              </Button>
              
              <Button
                size="lg"
                className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-6 gap-2 group"
                onClick={() => {
                  trackBookRoadmapClicked();
                  window.open('https://offer.trilogyoneconsulting.com', '_blank');
                }}
                data-testid="book-roadmap-cta"
              >
                Book AI Opportunity Roadmap
                <ArrowUpRight className="h-5 w-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Button>
            </div>
            
            <div className="mt-4">
              <Button
                size="lg"
                variant="outline"
                className="w-full py-6 gap-2"
                onClick={() => {
                  trackScheduleCallClicked();
                  window.open('https://calendly.com/trilogyoneconsulting/ai-audit-15', '_blank');
                }}
                data-testid="schedule-call-cta"
              >
                <Calendar className="h-5 w-5" />
                Schedule 15-Min Call
              </Button>
            </div>

            <div className="text-center text-sm text-muted-foreground mt-6">
              <p>Questions? Email us at <a href="mailto:hello@trilogyoneconsulting.com" className="text-primary hover:underline">hello@trilogyoneconsulting.com</a></p>
            </div>
          </div>
        </div>
      </div>
    </PageFrame>
  );
}
